
import { ChangeEvent, useEffect, useState } from 'react';
import { InView, useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { VideoStepperProps } from './query';
import Button_v2 from '@/components/molecules/Button/index';
import Text from '@/components/molecules/Text';
import { Svg } from '@/components/styled';
import { List as IconsList } from '@/public/img/icons';
import { ButtonProps } from '@/queries/components/molecules';
import devices from '@/utilities/devices';
import { renderIcons } from '@/utilities/functions';

// #region STYLE
const VideoStepperContainer = styled.div`
	display: flex;
	margin-top: 3rem;
	a {
		margin: 2.5rem 0 0;
	}

	@media screen and (${devices.mobile}) {
		display: none;
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		display: none;
	}
`;

const StepsContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	width: 60%;
`;

const StepBlock = styled.div<{ isActive: boolean }>`
	align-items: center;
	display: flex;
	border-radius: 4px;
	box-sizing: border-box;
	cursor: pointer;
	margin: 0.75rem 0;
	padding: 1.2rem 1.8rem;
	transition: .3s;
	user-select: none;
	width: 100%;
	z-index: 1;
	-webkit-tap-highlight-color: transparent;
	-webkit-touch-callout: none;
	-moz-tap-highlight-color: transparent;
	-moz-touch-callout: none;

	svg {
		padding-right: .8rem;
		flex-shrink: 0;
	}
	:nth-child(1n) {
		background-color: ${({ theme, isActive }) => isActive ? theme.color.beige : theme.color.white};
		border: ${({ theme, isActive }) => isActive ? `2px solid ${theme.color.beige}` : `2px solid ${theme.color.greyLight}`};
		box-shadow: ${({ isActive }) => isActive ? '0px 0px 10px rgba(63, 6, 43, 0.1)' : ''};
		color: ${({ theme, isActive }) => isActive ? theme.color.black : theme.color.grey};
		transform: ${({ isActive }) => isActive ? 'translateY(-3px)' : 'translateY(0px)'};
		svg {
			fill: ${({ theme, isActive }) => isActive ? theme.color.prune : theme.color.grey};
			circle {
				stroke: ${({ theme, isActive }) => isActive ? theme.color.prune : theme.color.grey};
			}
			path {
				fill: ${({ theme, isActive }) => isActive ? theme.color.prune : theme.color.grey};
				transition: .3s;
			}
		}
	}

	@media screen and (${devices.mobile}) {
		flex-direction: column;
		opacity: ${({ isActive }) => isActive ? 1 : 0};
		padding: 1.2rem .5rem;
		text-align: center;
		transform: translateY(${({ isActive }) => isActive ? '0px' : '30px'});
		transition: opacity transform .65s;
		svg {
			padding-bottom: .8rem;
			padding-right: 0;
		}
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		opacity: ${({ isActive }) => isActive ? 1 : 0};
		padding: 1.2rem 1rem;
		transform: translateY(${({ isActive }) => isActive ? '0px' : '30px'});
		transition: opacity transform .65s;
		svg {
			padding-right: .8rem;
		}
	}
`;

const VideoContainer = styled.div`
	animation: ease fadeIn .5s forwards .3s;
	background-color: ${({ theme }) => theme.color.white};
	opacity: 0;
	overflow: hidden;
	width: 40%;
	z-index: 0;
	display: flex;
	justify-content: center;

	@keyframes fadeIn {
		0% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}

	video {
		width: 67%;
		height: auto;
		position: relative;
		left: 15px;
		top: 0px;
	}

	.placeholder {
		width: 67%;
		height: auto;
		position: relative;
		background-color: ${({ theme }) => theme.color.white};
	}
`;

const MobileStepperContainer = styled.div`
	display: none;
	flex-direction: column;
	margin-top: 1.5rem;

	a {
		margin: 1rem 0 0;
	}

	@media screen and (${devices.mobile}) {
		display: flex;
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		display: flex;
	}
`;
// #endregion

interface VideoStepperOwnProps {
	videoSteps: VideoStepperProps[];
	description?: string;
	buttons: ButtonProps[];
}

// #region COMPONENT
const VideoStepper = ({
	videoSteps,
	description,
	buttons,
}: VideoStepperOwnProps) => {

	// #region HOOKS
	const [activeStep, setActiveStep] = useState<number>(0);
	const [videoMetadata, setVideoMetadata] = useState<{ duration: number; currentTime: number }>({
		currentTime: 0,
		duration: 0,
	});
	const [videoStepperRef, videoStepperInView] = useInView();
	// #endregion

	// #region METHOD
	const nextStep = () => (
		activeStep === (videoSteps.length - 1) ? (
			setActiveStep(0)
		) : (
			setActiveStep(activeStep + 1)
		)
	);
	// #endregion

	// #region LIFECYCLE
	useEffect(
		() => {
			const automaticNextVideo = setTimeout(() => videoMetadata.duration && nextStep(), (videoMetadata.duration * 1000));

			return () => clearTimeout(automaticNextVideo);
		},
		[videoMetadata],
	);
	// #endregion

	// #region RENDER
	return (
		<>
			<VideoStepperContainer ref={videoStepperRef}>
				<StepsContainer>
					{description && (
						<Text>
							{description}
						</Text>
					)}
					{videoSteps.map(
						(step, index) => (
							<StepBlock
								isActive={activeStep === index}
								key={index}
								onClick={() => setActiveStep(index)}
							>
								<Svg
									size={{
										height: '42px',
										width: '42px',
									}}
								>
									{renderIcons(step.icon.name, IconsList)}
								</Svg>
								{step.description}
							</StepBlock>
						),
					)}
					{
						buttons && buttons.map(
							(button, index) => (
								<Button_v2
									gtm_id={button.trackerId}
									href={button.url}
									key={index}
									openOnNewTab={button.newTab}
									text={button.text}
									version={button.variant}
								/>
							),
						)
					}
				</StepsContainer>

				<VideoContainer key={activeStep}>
					{(videoStepperInView) ? (
						<video
							autoPlay
							loop
							muted
							onLoadedMetadata={(event: ChangeEvent<HTMLVideoElement>) => setVideoMetadata({
								currentTime: event.target.currentTime,
								duration: event.target.duration,
							})}
							preload='auto'
							src={videoSteps[activeStep].videoSource.data.attributes.url}
						/>
					) : (
						<div className='placeholder' />
					)}
				</VideoContainer>
			</VideoStepperContainer>

			<MobileStepperContainer>
				{description && (
					<Text textAlign='center'>
						{description}
					</Text>
				)}
				{videoSteps.map(
					(step, index) => (
						<InView key={index} threshold={1} triggerOnce rootMargin='-100px 0px'>
							{({ inView, ref }) => (
								<StepBlock
									isActive={inView}
									ref={ref}
								>
									<Svg
										size={{
											height: '42px',
											width: '42px',
										}}
									>
										{renderIcons(step.icon.name, IconsList)}
									</Svg>
									{step.description}
								</StepBlock>
							)}
						</InView>
					),
				)}
				{
					buttons && buttons.map(
						(button, index) => (
							<Button_v2
								gtm_id={button.trackerId}
								href={button.url}
								key={index}
								openOnNewTab={button.newTab}
								text={button.text}
								version={button.variant}
							/>
						),
					)
				}
			</MobileStepperContainer>
		</>
	);
	// #endregion
};
// #endregion

export default VideoStepper;
